import { ISOFormatWithSec, MaxDate, MinDate, TimeSpanMaxValue, UTCFormatWithSec } from 'mbs-ui-shared@constants/c-sharp-constants';
import { windowsUpperCaseInvalidPath } from 'mbs-ui-shared@constants/folders-path-constants';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import {
  GetTimeSpanString,
  baseDateFormatWithSeconds,
  cobbledDateFormat,
  dateTimePointsFormat,
  dateTimePointsReg,
  getFullDateFromDateTimeObject,
  getFullISODateFromDateTimeObject
} from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { EC2StepValue } from 'wizards/models/EC2-models';
import { AzureStepValue } from 'wizards/models/azure-models';
import { BackupToRestoreStepValue } from 'wizards/models/backup-to-restore-models';
import { RestorePlanType } from 'wizards/models/base/base-models/plan-restore-plan-type-model';
import { RestoreToAzureArgs } from 'wizards/models/base/base-models/plan-restore-to-azure-args-model';
import { RestoreToEC2Args } from 'wizards/models/base/base-models/plan-restore-to-ec2-args-model';
import { DefaultTemplate, DefaultTemplateMask, DestinationDBStepValue } from 'wizards/models/destination-db-models';
import { DestinationVMStepValue } from 'wizards/models/destination-vm-models';
import { PartitionsStepValue } from 'wizards/models/partitions-models';
import { PlanNameStepValue } from 'wizards/models/plan-name-models';
import { RestoreDisksStepValue } from 'wizards/models/restore-disks-models';
import { RestorePointStepValue, RestoreType } from 'wizards/models/restore-point-models';
import { ScheduleAdvancedStepValue } from 'wizards/models/schedule-advanced-models';
import { ScheduleStepValue, ScheduleType } from 'wizards/models/schedule-models';
import { HostTypeNumbers, SelectHostStepValue } from 'wizards/models/select-host-models';
import { VirtualDisksSelectedType } from 'wizards/models/select-virtual-disks-models';
import { TemporaryInstanceStepValue } from 'wizards/models/temporary-instance-models';
import {
  AzureRestoreTypeEnum,
  EC2RestoreTypeEnum,
  RestoreAsDiskEnum,
  RestoreTypeFromAgentEnum,
  TypeDataStepValue,
  VirtualDiskFileFormat
} from 'wizards/models/type-data-models';
import { DeletePlan } from '../../models/base/base-models/plan-restore-delete-plan-model';
import { DestinationStepValue } from '../../models/destination-models';
import { EncryptionOptionsStepValue } from '../../models/encryption-options-models';
import { RestoreSourceStepValue } from '../../models/restore-source-models';
import { SupportMethodsForPlanFromSteps } from '../support-methods-for-plan-from-steps';

export class RestorePlanFromStepsHelper {
  public static updateNewPlanByNameStep(newPlan, step: PlanNameStepValue): void {
    newPlan.Name = step.Name;
    newPlan.DeletePlan = step.SavePlanInCloud ? DeletePlan.Never : DeletePlan.OnSucceeds;
    newPlan.SavePlanInCloud = false;
  }

  public static getFileFormat(newPlan, step: TypeDataStepValue): VirtualDiskFileFormat {
    return newPlan.VirtualDiskFileFormatList && newPlan.VirtualDiskFileFormatList.length
      ? newPlan.VirtualDiskFileFormatList.find((f) => f.DisplayName === step.selectedFileFormat)
      : SupportMethodsForPlanFromSteps.getDiskImageFileFormat(step.selectedFileFormat);
  }

  public static updateNewPlanByTypeDataStep(newPlan, step: TypeDataStepValue, type?: PlanTypes): void {
    const isHyperV = type === PlanTypes.RestoreHyperVToCustom || type === PlanTypes.RestoreHyperV;

    switch (step.restoreAs) {
      case RestoreAsDiskEnum.Physical:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.PhysicalDisk;
        newPlan.DiskImageFileFormat = {
          DisplayName: 'Hyper-V Virtual Disk (VHDX-format) dynamic',
          Name: 'VHDX',
          UseBuilder: true,
          VariantName: 'dynamic'
        };
        break;
      case RestoreAsDiskEnum.Virtual:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.VirtualDisk;
        if (isHyperV || type === PlanTypes.RestoreVMware || type === PlanTypes.RestoreVMwareToCustom) {
          newPlan.RestorePlanType = RestorePlanType.VMToCloud;
          newPlan.HostType = isHyperV ? HostTypeNumbers.HyperV : HostTypeNumbers.VMWare;
          newPlan.RestoreToVirtualDisk = {
            DiskImageFileFormat: this.getFileFormat(newPlan, step),
            ExcludeDisks: [],
            GenericDiskAdapterType: 0
          };
        } else {
          newPlan.DiskImageFileFormat = this.getFileFormat(newPlan, step);
        }
        break;
      case RestoreAsDiskEnum.RestoreToHyperV:
        newPlan.RestorePlanType = RestorePlanType.HyperV;
        newPlan.ImportVm = false;
        break;
      case RestoreAsDiskEnum.RestoreToHyperVAndImport:
        newPlan.RestorePlanType = RestorePlanType.HyperV;
        newPlan.ImportVm = true;
        break;
      case RestoreAsDiskEnum.RestoreToVMware:
        newPlan.RestorePlanType = RestorePlanType.VMwareMachine;
        break;
      case RestoreAsDiskEnum.EC2:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToEC2;
        if (!newPlan.RestoreToEC2) newPlan.RestoreToEC2 = new RestoreToEC2Args();
        newPlan.RestoreToEC2.EC2RestoreType = step.createAMI ? EC2RestoreTypeEnum.EC2Image : EC2RestoreTypeEnum.EC2Instance;
        newPlan.DiskImageFileFormat = {
          DisplayName: 'Hyper-V Virtual Disk (VHDX-format) dynamic',
          Name: 'VHDX',
          UseBuilder: true,
          VariantName: 'dynamic'
        };
        break;
      case RestoreAsDiskEnum.EBSVolume:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToEC2;
        if (!newPlan.RestoreToEC2) newPlan.RestoreToEC2 = new RestoreToEC2Args();
        newPlan.RestoreToEC2.EC2RestoreType = EC2RestoreTypeEnum.EC2Volume;
        break;
      case RestoreAsDiskEnum.AVM:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToAzure;
        if (!newPlan.RestoreToAzure) newPlan.RestoreToAzure = new RestoreToAzureArgs();
        newPlan.RestoreToAzure.AzureRestoreType = AzureRestoreTypeEnum.AzureInstance;
        break;
      case RestoreAsDiskEnum.ADD:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToAzure;
        if (!newPlan.RestoreToAzure) newPlan.RestoreToAzure = new RestoreToAzureArgs();
        newPlan.RestoreToAzure.AzureRestoreType = AzureRestoreTypeEnum.AzureDataDisk;
        break;
    }
  }

  public static updatePlanFromAzureStep(plan, step: AzureStepValue): void {
    plan.RestoreToAzure.AzureVMConnectionID = step.account;
    plan.RestoreToAzure.InstanceName = step.computerName || step.dataDiskName;
    plan.RestoreToAzure.Location = step.locations;
    plan.RestoreToAzure.ResourceGroupName = step.resourceGroup;
    plan.RestoreToAzure.VMSize = step.vmSize;
    plan.RestoreToAzure.NetworkName = step.network;
    plan.RestoreToAzure.SubnetName = step.subnet;
    plan.RestoreToAzure.StorageName = step.storage;
    plan.RestoreToAzure.BucketName = step.container;
    plan.RestoreToAzure.BootDiagnosticStorageName = step.bootDiagnosticStorage;
  }

  public static updatePlanFromEC2(plan, step: EC2StepValue): void {
    plan.RestoreToEC2.EC2ConnectionId = step.account;
    plan.RestoreToEC2.RegionName = step.region;
    plan.RestoreToEC2.AvailabilityZone = step.availabilityZones;
    plan.RestoreToEC2.InstanceType = step.instanceType;
    plan.RestoreToEC2.SubnetId = step.subnet;
    plan.RestoreToEC2.SecurityGroupId = step.securityGroup;
  }

  public static updatePlanFromTemporaryInstance(plan, step: TemporaryInstanceStepValue): void {
    plan.RestoreToEC2TemporaryInstance.IsRemoteRestore = step.useTemporary;
    plan.RestoreToEC2TemporaryInstance.EC2ConnectionId = step.account;
    plan.RestoreToEC2TemporaryInstance.RegionName = step.region;
    plan.RestoreToEC2TemporaryInstance.AvailabilityZone = step.availabilityZones;
    plan.RestoreToEC2TemporaryInstance.InstanceType = step.instanceType;
    plan.RestoreToEC2TemporaryInstance.SubnetId = step.subnet;
    plan.RestoreToEC2TemporaryInstance.SecurityGroupId = step.securityGroup;
    plan.RestoreToEC2TemporaryInstance.AMI = step.ami;
  }

  public static updateNewPlanByPartitionsStep(newPlan, partitionsStep: PartitionsStepValue): void {
    newPlan.DiskInfo =
      partitionsStep.restoreParentPartitions && partitionsStep.restoreParentPartitions.length
        ? partitionsStep.restoreParentPartitions.sort((a, b) => a.diskNumber - b.diskNumber)
        : [];
    newPlan.ConvertToMBR = partitionsStep.convertToMBR;
  }

  public static updateNewPlanByDestinationStep(
    newPlan,
    step: DestinationStepValue,
    partitionsStep: PartitionsStepValue,
    forIBB = true,
    needRestoreDeleted = true
  ): void {
    if (forIBB) {
      newPlan.Destination = step.destinationFolder;
      newPlan.SpecificPartitions = newPlan.DiskImageRestoreType !== RestoreTypeFromAgentEnum.VirtualDisk && !step.isPhysical;
      newPlan.OverwriteExisting = newPlan.DiskImageRestoreType === RestoreTypeFromAgentEnum.VirtualDisk;
      // newPlan.VirtualImageFileName = step.virtualImageName;

      const newDisks: any[] = [];
      partitionsStep.restorePartitions.forEach((disk) => {
        const newValue: any = newDisks.length
          ? newDisks[0]
          : {
              Capacity: step.capacity ? step.capacity * 1024 * 1024 * 1024 : 0,
              RestoreVolumes: [],
              SourceDiskId: disk.diskId
            };
        if (newPlan.DiskImageRestoreType !== RestoreTypeFromAgentEnum.VirtualDisk) {
          const idx =
            step.selectedSpecificPartitions && step.selectedSpecificPartitions.length
              ? step.selectedSpecificPartitions.findIndex((p) => {
                  const pData = p.split('--');
                  return pData.length && pData[2] && pData[2].includes(disk.identityForUiField);
                })
              : -1;
          if (!step.isPhysical && idx !== -1) {
            const pData = step.selectedSpecificPartitions[idx].split('--');
            const what = pData[2].split('__');
            const to = pData[0].split('__');
            newValue.DestinationDiskId = to[1];
            newValue.RestoreVolumes.push({
              Length: disk.length,
              ExcludeRules: partitionsStep.restoreConfigs[`${disk.diskId}__${disk.identity}`] || [],
              SourceVolumeId: { Identity: what[0], Enabled: true, DiskId: what[1], Supported: disk.supported },
              DestinationVolumeId: { Identity: to[0], Enabled: true, DiskId: to[1], Supported: disk.supported }
            });
          } else {
            newValue.DestinationDiskId = step.selectedDisk;
            newValue.RestoreVolumes.push({
              Length: disk.length,
              ExcludeRules: partitionsStep.restoreConfigs[`${disk.diskId}__${disk.identity}`] || [],
              SourceVolumeId: { Identity: disk.identity, Enabled: true, DiskId: disk.diskId, Supported: disk.supported },
              DestinationVolumeId: null
            });
          }
        } else {
          newValue.Name = step.virtualImageName;
          newValue.RestoreVolumes.push({
            Length: disk.length,
            ExcludeRules: partitionsStep.restoreConfigs[`${disk.diskId}__${disk.identity}`] || [],
            SourceVolumeId: { Identity: disk.identity, Enabled: true, DiskId: disk.diskId, Supported: disk.supported },
            DestinationVolumeId: null
          });
        }
        if (!newDisks.length) newDisks.push(newValue);
      });
      newPlan.RestoreDiskConfiguration = newDisks;
    } else {
      newPlan.RestoreToOriginalLocation = !!step.toOriginalLocation;
      if (!step.toOriginalLocation) newPlan.Destination = step.destinationFolder;
      if (needRestoreDeleted) newPlan.RestoreDeletedFiles = !!step.restoreDeletedFiles;
      newPlan.SkipExistingFiles = !step.overwriteExistingFiles;
      if (step.overwriteExistingFiles) newPlan.OnlyNewFiles = step.restoreOnlyNewFiles;
      else newPlan.OnlyNewFiles = false;
      newPlan.RestoreNTFSPermissions = !!step.restoreNTFSPermissions;
    }
  }

  public static updateNewPlanByEncryptionOptionsStep(newPlan, step: EncryptionOptionsStepValue): void {
    if (!(step.isCryptWithPassword === null || step.isCryptWithPassword === undefined)) {
      newPlan.UseEncryption = step.isCryptWithPassword;
      if (newPlan.UseEncryption) {
        SupportMethodsForPlanFromSteps.updatePlanPassword(newPlan, step.password);
      } else {
        delete newPlan.EncryptionPassword;
      }
    } else {
      newPlan.UseEncryption = !!step.password;
      SupportMethodsForPlanFromSteps.updatePlanPassword(newPlan, step.password);
    }
  }

  public static updateNewPlanByRestorePointDBStep(newPlan, step: RestorePointStepValue, timeZoneOffset: number): void {
    newPlan.RestoreType = step.restoreType;

    if (step.restoreType === RestoreType.LatestVersion) {
      newPlan.PointInTime = MaxDate;
      return;
    }

    newPlan.PointInTime = moment
      .utc(getFullISODateFromDateTimeObject(step.fromDateTime))
      .utcOffset(timeZoneOffset * -1)
      .format(UTCFormatWithSec);
    newPlan.UseBackupDate = false;
    newPlan.RestoreFromPointInTime = MinDate;
  }

  public static updateNewPlanByRestorePointStep(
    newPlan,
    step: RestorePointStepValue,
    settings: WizardHelpersSettings,
    isIbb = false
  ): void {
    if (step.restoreType === RestoreType.Manually) {
      newPlan.RestoreType = step.restoreType;
      newPlan.UseBackupDate = false;
      newPlan.RestoreFromPointInTime = MinDate;
      newPlan.PointInTime = isIbb ? moment(getFullDateFromDateTimeObject(step.fromDateTime, true)).format(ISOFormatWithSec) : MaxDate;
    } else if (step.restoreType === RestoreType.LatestVersion) {
      newPlan.RestoreType = step.restoreType;
      newPlan.PointInTime = MaxDate;
    } else if (step.restoreType === RestoreType.PointInTime) {
      newPlan.RestoreType = step.restoreType;
      if (settings.isNBF) {
        newPlan.PointInTime = moment(getFullDateFromDateTimeObject(step.fromDateTime, true)).format(ISOFormatWithSec);
      } else {
        newPlan.PointInTime = moment
          .utc(getFullISODateFromDateTimeObject(step.fromDateTime))
          .utcOffset(settings.timeZoneOffset * -1)
          .format(UTCFormatWithSec);
      }
      newPlan.UseBackupDate = false;
      newPlan.RestoreFromPointInTime = MinDate;
    } else if (step.restoreType === RestoreType.ModificationPeriod || step.restoreType === RestoreType.BackupPeriod) {
      newPlan.RestoreType = RestoreType.PointInTime;
      if (settings.isNBF) {
        newPlan.PointInTime = moment(getFullDateFromDateTimeObject(step.toDateTime, true)).format(ISOFormatWithSec);
        newPlan.RestoreFromPointInTime = moment(getFullDateFromDateTimeObject(step.fromDateTime, true)).format(ISOFormatWithSec);
      } else {
        newPlan.PointInTime = moment
          .utc(getFullISODateFromDateTimeObject(step.toDateTime))
          .utcOffset(settings.timeZoneOffset * -1)
          .format(UTCFormatWithSec);
        newPlan.RestoreFromPointInTime = moment
          .utc(getFullISODateFromDateTimeObject(step.fromDateTime))
          .utcOffset(settings.timeZoneOffset * -1)
          .format(UTCFormatWithSec);
      }
      newPlan.UseBackupDate = step.restoreType === RestoreType.BackupPeriod;
    }
    if (settings.isUnix && !settings.isNBF) newPlan.SyncBeforeRun = !!step.syncBeforeRun;
  }

  public static updateRestoreSourceDB(newPlan, databases: RestoreSourceStepValue, isUnix = false): void {
    const join: '/' | '\\' = isUnix ? '/' : '\\';

    newPlan.Databases = [];

    databases.folders.forEach((path: string) => {
      const pathArr = path.split(join);
      const lastIndex = pathArr.length > 3 ? 2 : pathArr.length - 1;

      newPlan.Databases.push({ InstanceName: pathArr.slice(0, lastIndex).join(join), DatabaseName: pathArr.slice(lastIndex).join(join) });
    });
  }

  public static updateRestoreSourceVM(newPlan, machines: RestoreSourceStepValue, disks: RestoreDisksStepValue, type: PlanTypes): void {
    if (
      type === PlanTypes.RestoreVMwareToCustom ||
      (newPlan.HostType === HostTypeNumbers.HyperV && newPlan.RestorePlanType === RestorePlanType.VMToCloud)
    ) {
      const Names: string[] = machines.folders[0].split('\\').filter((s: string) => !!s);

      if (Names?.length) {
        newPlan.HostName = Names[0] || '';
        newPlan.VmName = Names[1] || '';
      }

      return;
    }

    delete newPlan.HostName;
    delete newPlan.VmName;

    const machinesMap: { [key: string]: { HostName: string; VMName: string; NewVMName?: string; ExcludeDisks: string[] } } = {};

    machines.folders.forEach((path: string) => {
      const pathSlices = path.split('\\').filter((s: string) => !!s);

      if (!machinesMap[pathSlices[1]]) {
        machinesMap[pathSlices[1]] = { HostName: pathSlices[0], VMName: pathSlices[1], NewVMName: pathSlices[1], ExcludeDisks: [] };
      }
    });

    if (
      [RestorePlanType.HyperV, RestorePlanType.VMwareMachine].includes(newPlan.RestorePlanType) &&
      disks.type === VirtualDisksSelectedType.Selected
    ) {
      disks.excludes.forEach((path: string) => {
        const pathSlices = path.split('\\').filter((s: string) => !!s);

        if (!machinesMap[pathSlices[1]]) {
          machinesMap[pathSlices[1]] = {
            HostName: pathSlices[0],
            VMName: pathSlices[1],
            NewVMName: pathSlices[1],
            ExcludeDisks: [pathSlices[2]]
          };
        } else {
          machinesMap[pathSlices[1]].ExcludeDisks.push(pathSlices[2]);
        }
      });
    }

    newPlan.VirtualMachines = [];

    Object.keys(machinesMap).forEach((key: string) => newPlan.VirtualMachines.push(machinesMap[key]));
  }

  public static checkUnix(str: string): boolean {
    return str.startsWith('CBB_ROOT\\') || str.startsWith('CBB_VOLUMES\\') || str.startsWith('etc\\');
  }

  public static updateNewPlanByRestoreSourceStep(newPlan, step: RestoreSourceStepValue, isUnix = false, computerName = '', isNBF = false): void {
    const join = isUnix ? '/' : '\\';
    const unixOnWindowsPC = step.folders.every((str) => !isUnix && windowsUpperCaseInvalidPath.test(str));
    const prefixWithCompName = `\\\\${computerName}\\`;
    const getModifiedPath = (str: string) => (!isUnix && str.endsWith(join) ? str.substring(0, str.length - 1) : str);

    newPlan.Versions = [];
    newPlan.Items = [];

    if (unixOnWindowsPC && newPlan.RestoreType !== RestoreType.Manually) {
      newPlan.Items = step.folders.map((f) => ({ Path: (!isNBF && !this.checkUnix(f) ? prefixWithCompName : '') + getModifiedPath(f) }));

      return;
    }

    if (newPlan.RestoreType !== RestoreType.Manually) {
      newPlan.Items = step.folders.map((f) => ({ Path: getModifiedPath(f) }));

      return;
    }

    step.folders.forEach((str) => {
      const strArr = str.split(join);
      const lastElem = strArr[strArr.length - 1];
      const existNormal = dateTimePointsReg.test(lastElem);
      const existCobble = dateTimePointsFormat.test(lastElem);
      const exist = existNormal || existCobble;
      const result: { Path?: string; ModifyDate?: string } = {};

      if (exist) {
        const newPath = strArr.splice(0, strArr.length - 1).join(join);
        result.ModifyDate = existCobble ? moment.utc(lastElem, cobbledDateFormat).format(baseDateFormatWithSeconds) + 'Z' : lastElem;
        result.Path = getModifiedPath(newPath);

        if (unixOnWindowsPC) result.Path = (!isNBF && !this.checkUnix(result.Path) ? prefixWithCompName : '') + result.Path;

        return void newPlan.Versions.push(result);
      }

      result.Path = getModifiedPath(str);

      if (unixOnWindowsPC) result.Path = (!isNBF && !this.checkUnix(result.Path) ? prefixWithCompName : '') + result.Path;

      newPlan.Items.push(result);
    });
  }

  public static updateNewPlanByBackupToRestoreStep(newPlan, step: BackupToRestoreStepValue): void {
    newPlan.BackupPlanName = step.backupPlanName;
  }

  public static updateNewPlanByScheduleStep(
    newPlan,
    step: ScheduleStepValue,
    advancedStep: ScheduleAdvancedStepValue,
    isUnix = false,
    isNBF = false
  ): void {
    const recTypeNumber = +ScheduleType[step.ScheduleType];
    if (!isUnix || isNBF) newPlan.SyncBeforeRun = step.syncBeforeRun;
    if (recTypeNumber === ScheduleType.recurring || recTypeNumber == ScheduleType.once) {
      SupportMethodsForPlanFromSteps.SetPlanScheduleSettingsAdvanced(step, newPlan, advancedStep, isUnix, true);
    } else {
      newPlan.ForceFullSchedule = { Enabled: false };
      newPlan.Schedule = { Enabled: false };
      newPlan.ScheduleDiff = { Enabled: false };
      newPlan.ScheduleTLog = { Enabled: false };
      const stopAfterDuration = moment.duration({ hours: step.stopAfterHours, minutes: step.stopAfterMinutes });
      newPlan.Schedule = {
        Enabled: recTypeNumber !== ScheduleType.noschedule,
        StopAfter: !step.StopAfterEnabled ? TimeSpanMaxValue : GetTimeSpanString(stopAfterDuration)
      };
      if (recTypeNumber == ScheduleType.instantly) newPlan.ForceFullSchedule.RecurType = RecurType.Instantly;
      newPlan.ForceMissedSchedule = !!step.ForceMissedSchedule;
      newPlan.UseDifferentialUpload = false;
    }
    SupportMethodsForPlanFromSteps.delStopAfterTickFromPlan(newPlan);
  }

  public static updateRestoreDestinationVM(newPlan, step: DestinationVMStepValue, type: PlanTypes): void {
    newPlan[newPlan.RestorePlanType === RestorePlanType.HyperV ? 'RestorePath' : 'Destination'] = step.destinationFolder;
    newPlan.Overwrite = !!step.overwriteExistingVM;
    newPlan.PowerOnAfterRestore = !!step.powerOnVM;
    newPlan.RestoreToOriginalLocation = type === PlanTypes.RestoreHyperVToCustom ? false : !!step.toOriginalLocation;

    if (
      (newPlan.RestorePlanType !== RestorePlanType.HyperV || type === PlanTypes.RestoreHyperVToCustom || step.toOriginalLocation) &&
      newPlan.RestorePlanType !== RestorePlanType.VMwareMachine
    )
      return;

    const machinesHash: { [key: string]: { HostName: string; VMName: string; ExcludeDisks: string[]; NewVMName?: string } } = {};
    newPlan.VirtualMachines?.forEach((machine) => (machinesHash[`${machine.HostName}\\${machine.VMName}`] = machine));
    newPlan.Datastore = step.dataStore;

    const existUniq = step.specificList?.some((names: string) => {
      const namesArr: string[] = names.split('--');
      return machinesHash[namesArr[0]] && machinesHash[namesArr[0]].VMName !== namesArr[1];
    });

    if (!existUniq) return;

    step.specificList.forEach((names: string) => {
      const namesArr: string[] = names.split('--');
      newPlan.VirtualMachines.forEach((m) => {
        if (namesArr[0].startsWith(m.HostName) && namesArr[0].endsWith(m.VMName)) {
          m.NewVMName = namesArr[1];
        }
      });
    });
  }

  public static updateRestoreDestinationDB(newPlan, step: DestinationDBStepValue): void {
    newPlan.DataPath = step.destinationFolder;
    newPlan.LogPath = step.destinationFolderLog;
    newPlan.CloseConnection = step.closeConnection;
    newPlan.FileMask = step.backupTemplate || DefaultTemplateMask;
    newPlan.Overwrite = step.overwriteExisting;

    const dbHash: { [key: string]: { InstanceName: string; DatabaseName: string; NewDatabaseName?: string } } = {};
    newPlan.Databases.forEach((db) => (dbHash[`${db.InstanceName}\\${db.DatabaseName}`] = db));

    step.specificList.forEach((names: string) => {
      const namesArr: string[] = names.split('--');
      newPlan.Databases.forEach((m) => {
        if (namesArr[0].startsWith(m.InstanceName) && namesArr[0].endsWith(m.DatabaseName)) {
          m.NewDatabaseName = namesArr[1];
        }
      });
    });
  }

  public static updateRestoreDestinationFileDB(newPlan, step: DestinationDBStepValue): void {
    newPlan.Destination = step.destinationFolder;
    newPlan.CreateFolderForEachDatabase = step.createFolder;
    newPlan.FileNameTemplate = step.backupTemplate || DefaultTemplate;
  }

  public static updateHost(newPlan, step: SelectHostStepValue): void {
    newPlan.Host = step.server;
    newPlan.Login = step.login;
    newPlan.Password = step.password;
    newPlan.RestoreToHost = step.host;
  }
}
